import { atom } from 'jotai'

import store from '@/lib/configs/store-config'

export type ConnectionStatus = 'online' | 'reconnecting' | 'offline' | 'session-check-required'

/** Global connectivity state that drives the reconnecting banner. */
export const connectionStatusAtom = atom<ConnectionStatus>('online')

/** Pure: pick the degraded status from whether the browser still has a network. */
export const computeDegradedStatus = (online: boolean): ConnectionStatus =>
  online ? 'reconnecting' : 'offline'

/** Mark the app degraded — `offline` when the browser reports no network, else `reconnecting`. */
export const markConnectionDegraded = () => {
  const online = typeof navigator === 'undefined' ? true : navigator.onLine !== false
  if (store.get(connectionStatusAtom) === 'session-check-required') return
  store.set(connectionStatusAtom, computeDegradedStatus(online))
}

/** Mark the app recovered. */
export const markConnectionOnline = () => {
  store.set(connectionStatusAtom, 'online')
}

let connectionCheckHandler: (() => void) | null = null

/** useConnectionRecovery registers its loop here; returns the unregister. */
export const registerConnectionCheck = (handler: () => void) => {
  connectionCheckHandler = handler
  return () => {
    if (connectionCheckHandler === handler) connectionCheckHandler = null
  }
}

/**
 * Something just reached the backend (an API response, the socket authenticating). While the
 * bar is up that's evidence the network is back, so the recovery loop checks now instead of
 * waiting out its backoff. It doesn't mark online itself: the loop still confirms the session
 * and hands off to runtime recovery in order.
 */
export const noteConnectionActivity = () => {
  if (store.get(connectionStatusAtom) === 'online') return
  connectionCheckHandler?.()
}
